import { useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMe,
  listFinanceAccounts,
  createFinanceAccount,
  updateFinanceAccount,
  listTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  parseFinanceImport,
  commitFinanceImport,
  ApiError,
  type AccountType,
  type CashflowSection,
  type FinanceAccount,
  type FinanceTx,
  type ImportPreviewRow,
  type TxDirection,
} from "@/lib/api";
import { fmtMoney } from "@/lib/money";
import { Modal } from "@/components/Modal";
import { StatementsTab } from "@/pages/finance/StatementsTab";
import { BankFeedTab } from "@/pages/finance/BankFeedTab";
import { InsightsTab } from "@/pages/finance/InsightsTab";

const inputCls =
  "rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
const labelCls = "mb-1 block text-xs font-medium text-text-muted";

const TYPE_LABEL: Record<AccountType, string> = {
  asset: "Assets",
  liability: "Liabilities",
  income: "Income",
  expense: "Expenses",
  equity: "Equity",
};

const thisYearStart = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Finance's pages are routes, not local state, so the section nav on the left
 * can list them and each one is linkable. `/finance` is the ledger; the rest
 * hang off `/finance/:tab`.
 *
 * `feed` is routable but deliberately not in the nav (see lib/nav.ts) while
 * TrueLayer onboarding is pending — restore it there to re-advertise it.
 */
const PAGES = {
  ledger: {
    title: "Ledger",
    blurb:
      "Money in and out by category. Export CSV for the accountant at year-end.",
    render: () => <LedgerTab />,
  },
  statements: {
    title: "Statements",
    blurb: "Monthly profit and loss built from the ledger. Export to PDF or Excel.",
    render: () => <StatementsTab />,
  },
  insights: {
    title: "Insights",
    blurb: "The same ledger as charts — trends and category breakdowns by year.",
    render: () => <InsightsTab />,
  },
  accounts: {
    title: "Accounts",
    blurb: "The chart of accounts every transaction posts to.",
    render: () => <AccountsTab />,
  },
  feed: {
    title: "Bank feed",
    blurb: "Imported bank transactions awaiting reconciliation.",
    render: () => <BankFeedTab />,
  },
} as const;

type Tab = keyof typeof PAGES;

const isTab = (v: string | undefined): v is Tab => !!v && v in PAGES;

export function FinancePage() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { tab } = useParams<{ tab?: string }>();

  // An unknown segment is a stale or mistyped link, not an error page.
  if (tab !== undefined && !isTab(tab)) return <Navigate to="/finance" replace />;
  const page = PAGES[isTab(tab) ? tab : "ledger"];

  return (
    <div>
      <header className="mb-6">
        <h1 className="page-title">{page.title}</h1>
        <p className="mt-2 text-text-secondary">{page.blurb}</p>
      </header>

      {me && me.role !== "admin" ? (
        <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
          Finance is only visible to admins.
        </p>
      ) : (
        page.render()
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Ledger
   ════════════════════════════════════════════════════════════════════════ */
function LedgerTab() {
  const [from, setFrom] = useState(thisYearStart());
  const [to, setTo] = useState("");
  const [accountId, setAccountId] = useState("");
  const [editTx, setEditTx] = useState<FinanceTx | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ["fin-accounts"],
    queryFn: listFinanceAccounts,
  });
  const txKey = ["fin-txs", from, to, accountId] as const;
  const { data: txs, isLoading } = useQuery({
    queryKey: txKey,
    queryFn: () =>
      listTransactions({ from: from || undefined, to: to || undefined, accountId: accountId || undefined }),
  });

  const totals = useMemo(() => {
    let inSum = 0;
    let outSum = 0;
    for (const t of txs ?? []) {
      if (t.direction === "in") inSum += t.amount;
      else outSum += t.amount;
    }
    return { inSum, outSum, net: inSum - outSum };
  }, [txs]);

  return (
    <div>
      <QuickAdd accounts={accounts ?? []} />

      {/* Filters + summary */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* period */}
        <div className="flex items-center gap-2 rounded-token-md border border-border bg-bg-card px-3 py-2 transition-colors focus-within:border-accent">
          <i className="fa-regular fa-calendar text-xs text-text-muted" />
          <input
            type="date"
            className="bg-transparent text-sm text-text outline-none"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            title="From"
          />
          <span className="text-text-muted">–</span>
          <input
            type="date"
            className="bg-transparent text-sm text-text outline-none"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            title="To (empty = today)"
          />
        </div>
        {/* account */}
        <div className="flex items-center gap-2 rounded-token-md border border-border bg-bg-card px-3 py-2 transition-colors focus-within:border-accent">
          <i className="fa-solid fa-sitemap text-xs text-text-muted" />
          <select
            className="max-w-[170px] bg-transparent text-sm text-text outline-none"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">All accounts</option>
            {accounts?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        {(from !== thisYearStart() || to || accountId) && (
          <button
            onClick={() => {
              setFrom(thisYearStart());
              setTo("");
              setAccountId("");
            }}
            className="text-xs font-medium text-text-muted hover:text-text-heading"
          >
            Reset
          </button>
        )}
        <button
          onClick={() => setImportOpen(true)}
          className="rounded-token-md border border-border bg-bg-card px-3 py-2 text-sm font-medium text-accent transition-colors hover:border-accent"
          title="Import an HSBC statement CSV — duplicates are detected automatically"
        >
          <i className="fa-solid fa-file-import mr-1.5 text-xs" />
          Import CSV
        </button>
        <div className="ml-auto flex gap-5 text-sm">
          <span className="text-text-secondary">
            In <strong style={{ color: "var(--color-success)" }}>{fmtMoney(totals.inSum)}</strong>
          </span>
          <span className="text-text-secondary">
            Out <strong style={{ color: "var(--color-danger)" }}>{fmtMoney(totals.outSum)}</strong>
          </span>
          <span className="text-text-secondary">
            Net{" "}
            <strong className="text-text-heading">
              {totals.net >= 0 ? "" : "−"}
              {fmtMoney(Math.abs(totals.net))}
            </strong>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
        {isLoading ? (
          <div className="grid place-items-center py-16 text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin text-lg" />
          </div>
        ) : !txs || txs.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-text-muted">
            No transactions in this period — add the first one above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="w-28 px-4 py-2.5 font-semibold">Date</th>
                <th className="px-4 py-2.5 font-semibold">Description</th>
                <th className="w-44 px-4 py-2.5 font-semibold">Category</th>
                <th className="w-32 px-4 py-2.5 font-semibold">Paid via</th>
                <th className="w-28 px-4 py-2.5 text-right font-semibold">In</th>
                <th className="w-28 px-4 py-2.5 text-right font-semibold">Out</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setEditTx(t)}
                  className="cursor-pointer border-b border-border-light transition-colors hover:bg-bg-card-hover"
                >
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-muted">{t.date}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-text-heading">{t.description}</span>
                    {t.reference && (
                      <span className="ml-2 text-xs text-text-muted">#{t.reference}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary">{t.account?.name}</td>
                  <td className="px-4 py-2.5 text-text-muted">{t.paidAccount?.name}</td>
                  <td className="px-4 py-2.5 text-right font-medium" style={{ color: "var(--color-success)" }}>
                    {t.direction === "in" ? fmtMoney(t.amount) : ""}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium" style={{ color: "var(--color-danger)" }}>
                    {t.direction === "out" ? fmtMoney(t.amount) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editTx && (
        <TxModal accounts={accounts ?? []} tx={editTx} onClose={() => setEditTx(null)} />
      )}
      {importOpen && (
        <ImportModal accounts={accounts ?? []} onClose={() => setImportOpen(false)} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Bank-statement import wizard
   ════════════════════════════════════════════════════════════════════════ */
interface ReviewRow extends ImportPreviewRow {
  include: boolean;
  accountId: string;
}

function ImportModal({
  accounts,
  onClose,
}: {
  accounts: FinanceAccount[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [skipped, setSkipped] = useState(0);
  const [statementBalance, setStatementBalance] = useState<number | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    skippedDuplicates: number;
    paidAccountBalance: number;
  } | null>(null);

  const paidVia = accounts.filter(
    (a) => !a.archived && (a.type === "asset" || a.type === "liability"),
  );
  const bank = paidVia.find((a) => a.name === "Bank");
  const [paidAccountId, setPaidAccountId] = useState(bank?.id ?? paidVia[0]?.id ?? "");

  const categoriesFor = (direction: TxDirection) =>
    accounts.filter(
      (a) =>
        !a.archived &&
        (direction === "out"
          ? a.type === "expense"
          : a.type === "income" || a.type === "equity"),
    );

  async function onPick(file: File) {
    setParsing(true);
    setError(null);
    try {
      const res = await parseFinanceImport(file);
      setRows(
        res.rows.map((r) => ({
          ...r,
          include: !r.duplicate,
          accountId: r.suggestedAccountId ?? "",
        })),
      );
      setSkipped(res.skipped);
      setStatementBalance(res.statementBalance);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not parse that CSV");
    } finally {
      setParsing(false);
    }
  }

  const patchRow = (hash: string, fields: Partial<ReviewRow>) =>
    setRows((rs) => rs!.map((r) => (r.importHash === hash ? { ...r, ...fields } : r)));

  const ready = rows?.filter((r) => r.include) ?? [];
  const missingCategory = ready.filter((r) => !r.accountId).length;

  const commit = useMutation({
    mutationFn: () =>
      commitFinanceImport(
        ready.map((r) => ({
          date: r.date,
          description: r.description,
          amount: r.amount,
          direction: r.direction,
          accountId: r.accountId,
          importHash: r.importHash,
        })),
        paidAccountId,
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["fin-txs"] });
      qc.invalidateQueries({ queryKey: ["fin-balances"] });
      setResult(r);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Import failed"),
  });

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Import bank statement"
      footer={
        result ? (
          <button
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold"
            style={{ color: "var(--color-on-accent)" }}
            onClick={onClose}
          >
            Done
          </button>
        ) : rows ? (
          <>
            <span className="mr-auto self-center text-xs text-text-muted">
              {ready.length} selected
              {missingCategory > 0 && (
                <span className="text-warning"> · {missingCategory} missing a category</span>
              )}
            </span>
            <button
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ color: "var(--color-on-accent)" }}
              disabled={ready.length === 0 || missingCategory > 0 || commit.isPending}
              onClick={() => commit.mutate()}
            >
              {commit.isPending ? "Importing…" : `Import ${ready.length} transactions`}
            </button>
          </>
        ) : undefined
      }
    >
      {result ? (
        <div className="py-8 text-center">
          <i className="fa-solid fa-circle-check mb-3 text-3xl text-success" />
          <p className="text-sm text-text-heading">
            Imported <strong>{result.created}</strong> transaction
            {result.created === 1 ? "" : "s"}
            {result.skippedDuplicates > 0 &&
              ` · ${result.skippedDuplicates} duplicate${result.skippedDuplicates === 1 ? "" : "s"} skipped`}
            .
          </p>
          {/* Reconciliation: ledger balance vs the statement's running balance */}
          {statementBalance != null && (
            <p
              className="mx-auto mt-4 inline-block rounded-token-md px-4 py-2 text-sm"
              style={
                Math.abs(result.paidAccountBalance - statementBalance) < 0.01
                  ? {
                      background: "color-mix(in srgb, var(--color-success) 12%, transparent)",
                      color: "var(--color-success)",
                    }
                  : {
                      background: "color-mix(in srgb, var(--color-warning) 12%, transparent)",
                      color: "var(--color-warning)",
                    }
              }
            >
              {Math.abs(result.paidAccountBalance - statementBalance) < 0.01 ? (
                <>
                  <i className="fa-solid fa-scale-balanced mr-1.5" />
                  Reconciled — ledger balance {fmtMoney(result.paidAccountBalance)} matches
                  the statement.
                </>
              ) : (
                <>
                  <i className="fa-solid fa-triangle-exclamation mr-1.5" />
                  Ledger balance {fmtMoney(result.paidAccountBalance)} vs statement{" "}
                  {fmtMoney(statementBalance)} — {fmtMoney(Math.abs(result.paidAccountBalance - statementBalance))}{" "}
                  apart. Check unticked rows or older missing statements.
                </>
              )}
            </p>
          )}
        </div>
      ) : !rows ? (
        <div className="py-6">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={parsing}
            className="grid w-full place-items-center rounded-token-lg border-2 border-dashed border-border px-6 py-12 text-center transition-colors hover:border-accent"
          >
            {parsing ? (
              <span className="text-sm text-text-muted">
                <i className="fa-solid fa-circle-notch fa-spin mr-2" />
                Reading statement…
              </span>
            ) : (
              <>
                <i className="fa-solid fa-file-csv mb-3 text-3xl text-text-muted" />
                <span className="text-sm font-medium text-text-heading">
                  Choose your HSBC CSV export
                </span>
                <span className="mt-1 text-xs text-text-muted">
                  Business Internet Banking → Statements → Download CSV. Rows already in
                  the ledger are detected and skipped.
                </span>
              </>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void onPick(f);
            }}
          />
          {error && <p className="mt-3 text-sm text-danger">{error}</p>}
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-text-secondary">
            <span>
              <strong className="text-text-heading">{rows.length}</strong> rows parsed
              {skipped > 0 && ` · ${skipped} unreadable lines skipped`}
              {rows.some((r) => r.duplicate) &&
                ` · ${rows.filter((r) => r.duplicate).length} already imported (unchecked)`}
            </span>
            <label className="ml-auto flex items-center gap-2">
              Paid via
              <select
                className="rounded-token-md border border-border bg-bg px-2 py-1 text-xs text-text outline-none focus:border-accent"
                value={paidAccountId}
                onChange={(e) => setPaidAccountId(e.target.value)}
              >
                {paidVia.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-[0.6rem] uppercase tracking-wider text-text-muted">
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={rows.every((r) => r.include || r.duplicate)}
                    onChange={(e) =>
                      setRows((rs) =>
                        rs!.map((r) => ({ ...r, include: e.target.checked && !r.duplicate })),
                      )
                    }
                  />
                </th>
                <th className="w-24 px-2 py-2 font-semibold">Date</th>
                <th className="px-2 py-2 font-semibold">Description</th>
                <th className="w-24 px-2 py-2 text-right font-semibold">Amount</th>
                <th className="w-44 px-2 py-2 font-semibold">Category</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.importHash}
                  className={`border-b border-border-light ${
                    r.duplicate ? "opacity-45" : ""
                  }`}
                >
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={r.include}
                      onChange={(e) => patchRow(r.importHash, { include: e.target.checked })}
                    />
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-text-muted">{r.date}</td>
                  <td className="max-w-0 px-2 py-1.5">
                    <span className="block truncate text-text" title={r.description}>
                      {r.description}
                    </span>
                    {r.duplicate && (
                      <span className="text-[0.6rem] font-semibold text-text-muted">
                        already imported
                      </span>
                    )}
                    {!r.duplicate && r.likelyDuplicate && (
                      <span className="text-[0.6rem] font-semibold text-warning">
                        similar entry exists — check before importing
                      </span>
                    )}
                  </td>
                  <td
                    className="whitespace-nowrap px-2 py-1.5 text-right font-medium"
                    style={{
                      color:
                        r.direction === "in" ? "var(--color-success)" : "var(--color-danger)",
                    }}
                  >
                    {r.direction === "in" ? "+" : "−"}
                    {fmtMoney(r.amount)}
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className={`w-full rounded border bg-bg px-1.5 py-1 text-xs outline-none focus:border-accent ${
                        r.include && !r.accountId ? "border-warning" : "border-border"
                      }`}
                      value={r.accountId}
                      onChange={(e) => patchRow(r.importHash, { accountId: e.target.value })}
                    >
                      <option value="">Choose…</option>
                      {categoriesFor(r.direction).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {error && <p className="mt-3 text-sm text-danger">{error}</p>}
        </>
      )}
    </Modal>
  );
}

/** One-line entry form — the daily-use surface, mirroring the old Excel row. */
function QuickAdd({ accounts }: { accounts: FinanceAccount[] }) {
  const qc = useQueryClient();
  const [direction, setDirection] = useState<TxDirection>("out");
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [accountId, setAccountId] = useState("");
  const [paidAccountId, setPaidAccountId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const categories = accounts.filter(
    (a) =>
      !a.archived &&
      (direction === "in"
        ? a.type === "income" || a.type === "equity"
        : a.type === "expense"),
  );
  const paidVia = accounts.filter(
    (a) => !a.archived && (a.type === "asset" || a.type === "liability" || a.type === "equity"),
  );
  const bank = paidVia.find((a) => a.name === "Bank");

  const create = useMutation({
    mutationFn: () =>
      createTransaction({
        date,
        description,
        amount: Number(amount),
        direction,
        accountId,
        paidAccountId: paidAccountId || bank?.id || "",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fin-txs"] });
      qc.invalidateQueries({ queryKey: ["fin-balances"] });
      setDescription("");
      setAmount("");
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not add transaction"),
  });

  const dirColor = direction === "in" ? "var(--color-success)" : "var(--color-danger)";

  return (
    <form
      className="mb-5 overflow-hidden rounded-token-lg border border-border bg-bg-card"
      onSubmit={(e) => {
        e.preventDefault();
        if (description.trim() && amount && accountId) create.mutate();
      }}
    >
      <div className="flex items-center gap-2 border-b border-border-light px-4 py-2.5">
        <i className="fa-solid fa-circle-plus text-xs text-accent" />
        <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Add transaction
        </span>
        {error && (
          <span className="ml-auto text-xs text-danger">
            <i className="fa-solid fa-triangle-exclamation mr-1" />
            {error}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4">
        {/* direction: segmented control */}
        <div>
          <label className={labelCls}>Money</label>
          <div className="flex overflow-hidden rounded-token-md border border-border">
            {(["out", "in"] as TxDirection[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setDirection(d);
                  setAccountId("");
                }}
                className="px-3 py-2 text-sm font-medium transition-colors"
                style={
                  direction === d
                    ? {
                        background: d === "in" ? "var(--color-success)" : "var(--color-danger)",
                        color: "#fff",
                      }
                    : { color: "var(--color-text-secondary)" }
                }
              >
                {d === "in" ? "In" : "Out"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Date</label>
          <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div className="min-w-[220px] flex-1">
          <label className={labelCls}>Description</label>
          <input
            className={`${inputCls} w-full`}
            placeholder={direction === "in" ? "e.g. Acme — launch video invoice" : "e.g. Adobe subscription"}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>
        <div>
          <label className={labelCls}>Amount</label>
          <div className="relative">
            <span
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium"
              style={{ color: dirColor }}
            >
              £
            </span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              className={`${inputCls} w-28 pl-7`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select className={inputCls} value={accountId} onChange={(e) => setAccountId(e.target.value)} required>
            <option value="" disabled>
              Choose…
            </option>
            {categories.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Paid via</label>
          <select
            className={inputCls}
            value={paidAccountId || bank?.id || ""}
            onChange={(e) => setPaidAccountId(e.target.value)}
          >
            {paidVia.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className={`fa-solid ${create.isPending ? "fa-circle-notch fa-spin" : "fa-plus"} mr-1.5 text-xs`} />
          {create.isPending ? "Adding…" : "Add"}
        </button>
      </div>
    </form>
  );
}

/** Edit / delete an existing transaction. */
function TxModal({
  accounts,
  tx,
  onClose,
}: {
  accounts: FinanceAccount[];
  tx: FinanceTx;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [direction, setDirection] = useState<TxDirection>(tx.direction);
  const [date, setDate] = useState(tx.date);
  const [description, setDescription] = useState(tx.description);
  const [amount, setAmount] = useState(String(tx.amount));
  const [accountId, setAccountId] = useState(tx.accountId);
  const [paidAccountId, setPaidAccountId] = useState(tx.paidAccountId);
  const [reference, setReference] = useState(tx.reference ?? "");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["fin-txs"] });
    qc.invalidateQueries({ queryKey: ["fin-balances"] });
  };

  const save = useMutation({
    mutationFn: () =>
      updateTransaction(tx.id, {
        date,
        description,
        amount: Number(amount),
        direction,
        accountId,
        paidAccountId,
        reference: reference || null,
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not save"),
  });
  const remove = useMutation({
    mutationFn: () => deleteTransaction(tx.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const categories = accounts.filter(
    (a) =>
      direction === "in" ? a.type === "income" || a.type === "equity" : a.type === "expense",
  );
  const paidVia = accounts.filter(
    (a) => a.type === "asset" || a.type === "liability" || a.type === "equity",
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit transaction"
      footer={
        <>
          <button
            className="mr-auto rounded-md px-3 py-1.5 text-sm text-danger hover:bg-danger/10"
            onClick={() => {
              if (confirm("Delete this transaction?")) remove.mutate();
            }}
          >
            <i className="fa-solid fa-trash mr-1.5 text-xs" />
            Delete
          </button>
          <button
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-heading"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="mb-3 flex gap-3">
        <div className="flex-1">
          <label className={labelCls}>Direction</label>
          <select
            className={`${inputCls} w-full`}
            value={direction}
            onChange={(e) => setDirection(e.target.value as TxDirection)}
          >
            <option value="out">Money out</option>
            <option value="in">Money in</option>
          </select>
        </div>
        <div className="flex-1">
          <label className={labelCls}>Date</label>
          <input type="date" className={`${inputCls} w-full`} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="flex-1">
          <label className={labelCls}>Amount (£)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            className={`${inputCls} w-full`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
      </div>

      <label className={labelCls}>Description</label>
      <input
        className={`${inputCls} mb-3 w-full`}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div className="mb-3 flex gap-3">
        <div className="flex-1">
          <label className={labelCls}>Category</label>
          <select
            className={`${inputCls} w-full`}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {categories.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className={labelCls}>Paid via</label>
          <select
            className={`${inputCls} w-full`}
            value={paidAccountId}
            onChange={(e) => setPaidAccountId(e.target.value)}
          >
            {paidVia.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className={labelCls}>Reference</label>
          <input
            className={`${inputCls} w-full`}
            placeholder="Invoice / receipt no."
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="text-xs text-danger">{error}</p>}
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Accounts (chart of accounts)
   ════════════════════════════════════════════════════════════════════════ */
function AccountsTab() {
  const qc = useQueryClient();
  const { data: accounts } = useQuery({
    queryKey: ["fin-accounts"],
    queryFn: listFinanceAccounts,
  });
  const [name, setName] = useState("");
  const [type, setType] = useState<AccountType>("expense");

  const create = useMutation({
    mutationFn: () => createFinanceAccount({ name, type }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fin-accounts"] });
      setName("");
    },
  });
  const update = useMutation({
    mutationFn: (vars: {
      id: string;
      fields: { name?: string; archived?: boolean; cashflowSection?: CashflowSection | null };
    }) => updateFinanceAccount(vars.id, vars.fields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fin-accounts"] });
      qc.invalidateQueries({ queryKey: ["fin-statements"] });
    },
  });

  const types: AccountType[] = ["asset", "liability", "income", "expense", "equity"];

  return (
    <div>
      <form
        className="mb-5 flex flex-wrap items-end gap-3 rounded-token-lg border border-border bg-bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <div className="min-w-[200px] flex-1">
          <label className={labelCls}>New account</label>
          <input
            className={`${inputCls} w-full`}
            placeholder="e.g. Contractors"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select className={inputCls} value={type} onChange={(e) => setType(e.target.value as AccountType)}>
            {types.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ color: "var(--color-on-accent)" }}
        >
          {create.isPending ? "Adding…" : "Add account"}
        </button>
      </form>

      <div className="grid gap-4 lg:grid-cols-2">
        {types.map((t) => {
          const group = accounts?.filter((a) => a.type === t) ?? [];
          if (group.length === 0) return null;
          return (
            <div key={t} className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
              <div className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
                {TYPE_LABEL[t]}
              </div>
              <ul className="divide-y divide-border-light">
                {group.map((a) => (
                  <li key={a.id} className="group flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span
                      className={a.archived ? "text-text-muted line-through" : "text-text-heading"}
                    >
                      {a.name}
                    </span>
                    {a.code && <span className="text-xs text-text-muted">({a.code})</span>}
                    {a.type === "expense" && !a.archived && (
                      <select
                        title="Cash-flow statement section"
                        className="rounded border border-border bg-bg px-1 py-0.5 text-[0.65rem] text-text-muted outline-none focus:border-accent"
                        value={a.cashflowSection ?? ""}
                        onChange={(e) =>
                          update.mutate({
                            id: a.id,
                            fields: {
                              cashflowSection: (e.target.value ||
                                null) as CashflowSection | null,
                            },
                          })
                        }
                      >
                        <option value="">Auto</option>
                        <option value="operating">Operating</option>
                        <option value="investing">Investing</option>
                      </select>
                    )}
                    <div className="ml-auto flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={() => {
                          const next = prompt("Rename account", a.name);
                          if (next?.trim() && next !== a.name)
                            update.mutate({ id: a.id, fields: { name: next.trim() } });
                        }}
                        className="rounded-md px-2 py-1 text-xs text-text-muted hover:bg-bg-elevated hover:text-text-heading"
                      >
                        Rename
                      </button>
                      <button
                        onClick={() =>
                          update.mutate({ id: a.id, fields: { archived: !a.archived } })
                        }
                        className="rounded-md px-2 py-1 text-xs text-text-muted hover:bg-bg-elevated hover:text-text-heading"
                      >
                        {a.archived ? "Restore" : "Archive"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-text-muted">
        Archived accounts stay on old transactions but disappear from entry forms.
      </p>
    </div>
  );
}
